import React from 'react'

import {
  RendererErrorCode,
  RendererStatus,
} from '@sketch-hq/sketch-web-renderer'

// Constants
import { CANVAS_VIEW_BETA_DOCS } from 'modules/shares/PageCanvasView/constants'

type RenderStatusError =
  | {
      type: 'FAILURE'
      code: RendererErrorCode
    }
  | {
      type: 'WEBGL_CONTEXT_LOST'
    }

export function getErrorDescription(
  rendererStatus: RenderStatusError
): React.ReactNode {
  if (rendererStatus.type === 'WEBGL_CONTEXT_LOST') {
    return (
      <>
        The canvas couldn’t load due to a problem with WebGL. Please reload — or
        try again later. <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a> about
        the canvas view beta.
      </>
    )
  }

  switch (rendererStatus.code) {
    case 'WASM_ERROR':
      return (
        <>
          The canvas couldn’t load due to a problem with Web Assembly. Please
          reload — or try again later.{' '}
          <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a> about the canvas view
          beta.
        </>
      )
    case 'FILE_ERROR':
      return (
        <>
          There was a problem loading the canvas data. Please reload — or try
          again later. <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a> about the
          canvas view beta.
        </>
      )
    case 'WEBGL_ERROR':
      return (
        <>
          The canvas couldn’t load due to a problem with WebGL. Please reload —
          or try again later. <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a>{' '}
          about the canvas view beta.
        </>
      )
    case 'ERROR':
    default:
      return (
        <>
          Something went wrong while loading the canvas. Please reload — or try
          again later. <a href={CANVAS_VIEW_BETA_DOCS}>Learn more</a> about the
          canvas view beta.
        </>
      )
  }
}

type RenderStatusLoading = {
  type: ExtractSafe<
    RendererStatus['type'],
    'INIT' | 'LOADING_FILE' | 'DRAWING_FILE' | 'FILE_READY'
  >
}

export function getLoadingMessage(rendererStatus?: RenderStatusLoading) {
  if (!rendererStatus || rendererStatus.type === 'INIT') {
    return 'Initializing…'
  }

  if (rendererStatus.type === 'DRAWING_FILE') {
    return 'Drawing…'
  }

  return 'Loading…'
}
